import { useState } from 'react';
import { ArrowDownToLine, ArrowUpToLine, ListOrdered, X } from 'lucide-react';

interface QueuePositionPromptProps {
  songTitle: string;
  queueLength: number;
  busy: boolean;
  onPlace: (position: number) => void;
  onCancel: () => void;
}

export function QueuePositionPrompt({ songTitle, queueLength, busy, onPlace, onCancel }: QueuePositionPromptProps) {
  const lastPosition = queueLength + 1;
  const [positionText, setPositionText] = useState('');
  const position = Number(positionText);
  const positionValid = Number.isInteger(position) && position >= 1 && position <= lastPosition;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4" data-testid="queue-position-prompt">
      <div className="bg-white dark:bg-gray-900 rounded-xl max-w-md w-full p-6 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center flex-shrink-0">
              <ListOrdered className="w-5 h-5 text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Where should &ldquo;{songTitle}&rdquo; go?
            </h3>
          </div>
          <button onClick={onCancel} className="p-1 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>
        <p className="text-gray-600 dark:text-gray-400 mb-4">
          Your queue is in manual order. Songs locked as next stay above it.
        </p>
        <div className="grid grid-cols-2 gap-3 mb-4">
          <button type="button" onClick={() => onPlace(1)} disabled={busy} className="btn btn-primary flex items-center justify-center gap-2 disabled:opacity-50">
            <ArrowUpToLine className="w-4 h-4" /> Top
          </button>
          <button type="button" onClick={() => onPlace(lastPosition)} disabled={busy} className="btn btn-primary flex items-center justify-center gap-2 disabled:opacity-50">
            <ArrowDownToLine className="w-4 h-4" /> Bottom
          </button>
        </div>
        <form
          className="flex items-end gap-3 mb-4"
          onSubmit={(event) => {
            event.preventDefault();
            if (positionValid && !busy) onPlace(position);
          }}
        >
          <label className="flex-1 text-sm text-gray-600 dark:text-gray-400" htmlFor="queue-position-input">
            Position (1–{lastPosition})
            <input
              id="queue-position-input"
              type="number"
              inputMode="numeric"
              min={1}
              max={lastPosition}
              value={positionText}
              onChange={(event) => setPositionText(event.target.value)}
              className="input mt-1 w-full"
            />
          </label>
          <button type="submit" disabled={!positionValid || busy} className="btn btn-secondary disabled:opacity-50 whitespace-nowrap">
            {positionValid ? `Put at #${position}` : 'Put here'}
          </button>
        </form>
        <button type="button" onClick={onCancel} className="btn btn-secondary w-full">
          Cancel
        </button>
      </div>
    </div>
  );
}
